import { Message, ScheduledMessage } from 'src/models/Message'
import { RoomType } from 'src/models/Room'
import { SWRResponse } from 'swr'
import { SWRInfiniteResponse } from 'swr/infinite'
import { scheduledMessageFormData, toScheduledMessage } from '.'
import { ApiClient } from '../client'
import {
  deleteScheduledMessage as deleteScheduledMessagePath,
  patchScheduledMessage as patchScheduledMessagePath,
  POST_SCHEDULED_SENDING_MESSAGE,
} from '../endpoints'
import {
  PostGroupMessageParam,
  PostGroupMessageResponse,
  PostScheduledMessageParam,
  PostScheduledMessageResponse,
  PutMessageParam,
  PutMessageResponse,
} from '../types/message'
import { groupRoomFormData, roomFormData, toMessage } from './converters'

const putMessage = async (
  param: PutMessageParam,
  mutate?: SWRInfiniteResponse<Message[]>['mutate'],
) => {
  const formData = roomFormData(param)
  const res = await ApiClient.put<PutMessageResponse>(
    `${param.url}.json`,
    formData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  )
  if (!mutate) return res
  await mutate(async (messages: Message[][] | undefined = []) => {
    if (!res.data) return messages
    if (!messages.some((array) => array.length))
      return [[toMessage(res.data.companyMessage)]]
    return [...messages, [toMessage(res.data.companyMessage)]]
  }, false)
  return res
}

const postGroupMessage = async (
  param: PostGroupMessageParam,
  mutate?: SWRInfiniteResponse<Message[]>['mutate'],
) => {
  const formData = groupRoomFormData(param)
  const res = await ApiClient.post<PostGroupMessageResponse>(
    `${param.url}.json`,
    formData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  )
  if (!mutate) return res
  await mutate(async (messages: Message[][] | undefined = []) => {
    if (!res.data) return messages
    if (!messages.some((array) => array.length))
      return [[toMessage(res.data.message)]]
    return [...messages, [toMessage(res.data.message)]]
  }, false)
  return res
}

const postScheduledMessage = async (
  param: PostScheduledMessageParam,
  mutate?: SWRResponse<ScheduledMessage[], any>['mutate'],
) => {
  const formData = scheduledMessageFormData(param)
  const res = await ApiClient.post<PostScheduledMessageResponse>(
    POST_SCHEDULED_SENDING_MESSAGE,
    formData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  )
  if (!mutate) return res
  await mutate(async (messages: ScheduledMessage[] | undefined = []) => {
    if (!res.data) return messages
    return [...messages, toScheduledMessage(res.data.scheduledSendingMessage)]
  }, false)
  return res
}

type SendMessageParam = (PutMessageParam | PostGroupMessageParam) & {
  roomType: RoomType
  scheduledAt?: Date
}

export const sendMessage = async (
  param: SendMessageParam,
  mutate?:
    | SWRResponse<ScheduledMessage[], any>['mutate']
    | SWRInfiniteResponse<Message[]>['mutate'],
) => {
  if (param.scheduledAt)
    return postScheduledMessage(
      param as PostScheduledMessageParam,
      mutate as SWRResponse<ScheduledMessage[], any>['mutate'],
    )
  if (param.roomType === 'CompanyMessageGroupRoom')
    return postGroupMessage(
      param,
      mutate as SWRInfiniteResponse<Message[]>['mutate'],
    )
  return putMessage(param, mutate as SWRInfiniteResponse<Message[]>['mutate'])
}

export const deleteMessage = async (
  url: string,
  uniqueId: string,
  mutate: SWRInfiniteResponse<Message[]>['mutate'],
) => {
  const res = await ApiClient.delete(`${url}.json`)
  if (res.error) return res
  await mutate(async (messages: Message[][] | undefined = []) => {
    for (const each of messages) {
      const target = each.find((message) => message.uniqueId === uniqueId)
      if (!target) continue
      target.deleted = true
      target.content = { text: 'このメッセージは削除されました' }
      break
    }
    return [...messages]
  }, false)
  return res
}

export const deleteScheduledMessage = async (
  id: number,
  mutate: SWRResponse<ScheduledMessage[], any>['mutate'],
) => {
  const res = await ApiClient.delete(deleteScheduledMessagePath(id))
  if (res.error) return res
  await mutate(async (messages: ScheduledMessage[] | undefined = []) => {
    if (!res.data) return messages
    return messages.filter((each) => each.id !== id)
  }, false)
  return res
}

export const patchScheduledMessage = async (
  id: number,
  text: string,
  mutate: SWRResponse<ScheduledMessage[], any>['mutate'],
) => {
  const formData = new FormData()
  formData.append('scheduled_sending_message[content]', text)
  const res = await ApiClient.patch(patchScheduledMessagePath(id), formData)
  if (res.error) return res
  await mutate(async (messages: ScheduledMessage[] | undefined = []) => {
    if (!res.data) return messages
    const target = messages.find((each) => each.id === id)
    if (target) target.content.text = text
    return messages
  }, false)
  return res
}
